import React, { useEffect, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X, Clock } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

const ToastComponent: React.FC<ToastProps> = ({ toast, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Slide in animation
    setTimeout(() => setIsVisible(true), 50);

    const duration = toast.duration || 5000;
    const interval = 50;
    const steps = duration / interval;
    const progressStep = 100 / steps;

    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - progressStep;
        if (newProgress <= 0) {
          clearInterval(progressInterval);
          handleDismiss();
          return 0;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(progressInterval);
  }, []);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => onDismiss(toast.id), 300);
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'info':
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getBackgroundColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-900/90 border-green-700';
      case 'error':
        return 'bg-red-900/90 border-red-700';
      case 'warning':
        return 'bg-yellow-900/90 border-yellow-700';
      case 'info':
      default:
        return 'bg-blue-900/90 border-blue-700';
    }
  };

  const getProgressColor = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'warning':
        return 'bg-yellow-500';
      case 'info':
      default:
        return 'bg-blue-500';
    }
  };

  return (
    <div
      className={`
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-2 opacity-0 scale-95'}
        max-w-md w-full shadow-xl rounded-lg pointer-events-auto
        ${getBackgroundColor()} border backdrop-blur-sm min-w-80
      `}
    >
      <div className="p-5">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="ml-4 flex-1 min-w-0">
            <p className="text-base font-semibold text-white leading-tight">
              {toast.title}
            </p>
            {toast.message && (
              <div className="mt-2 text-sm text-gray-300 leading-relaxed whitespace-pre-line break-words">
                {toast.message}
              </div>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              onClick={handleDismiss}
              className="inline-flex text-gray-400 hover:text-white focus:outline-none focus:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-1 w-full bg-gray-700 rounded-b-lg overflow-hidden">
        <div
          className={`h-full transition-all duration-50 ease-linear ${getProgressColor()}`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default ToastComponent;